'use client';

import { useCallback, useEffect, useState } from 'react';
import { LuChevronsUpDown } from 'react-icons/lu';
import { useProfileOrganizations } from '@core/ui/profile/ProfileOrganizationProvider';
import { useNavScrollOptional } from '@/contexts/NavScrollContext';
import { cn } from '@core/helpers/misc/GeneralUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';

/**
 * OrganizationSelector - Dropdown selector for switching organizations
 *
 * Always renders the same DropdownMenu structure to prevent flickering on load.
 * When not authenticated/loading, shows platform logo with hidden chevron and disabled dropdown.
 */
export function OrganizationSelector() {
  const { organizations, currentOrganization, selectOrganization } = useProfileOrganizations();
  const navScroll = useNavScrollOptional();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch from Radix auto-generated IDs
  useEffect(() => {
    setMounted(true);
  }, []);

  // Hide org name when breadcrumb is shown (to make room)
  const showOrgName = !navScroll?.headerHidden;

  // Use compact mode (avatar only) when action bar is in the nav menu
  const compactMode = navScroll?.actionBarHidden ?? false;

  // Check if this is the platform organization (or loading state shows platform)
  const isPlatformOrg = !currentOrganization || currentOrganization?.slug === 'inovisum';

  // Whether the selector is interactive (has organizations loaded)
  const isInteractive = organizations.length > 0 && currentOrganization;

  // Generate initials from organization name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const openCreateOrganization = () => {
    window.dispatchEvent(new CustomEvent('organization.settings.open'));
    // Small delay to ensure dialog is open before triggering create popup
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('organization.create.open'));
    }, 100);
  };

  // Control dropdown open state - only allow opening when interactive
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!isInteractive) return; // Block opening when not interactive
      setIsOpen(open);
    },
    [isInteractive]
  );

  // Shared trigger content to avoid duplication
  const triggerContent = (
    <>
      {/* Chevron on left - invisible when not interactive to maintain spacing */}
      <LuChevronsUpDown
        className={cn(
          'h-4 w-4 flex-shrink-0 transition-opacity',
          isInteractive ? 'text-foreground-subtle' : 'opacity-0'
        )}
      />

      {/* Logo for platform org, Avatar for other orgs */}
      {isPlatformOrg ? (
        // Platform org: show logo normally, avatar in compact mode
        compactMode ? (
          <div
            className={cn(
              'w-8 h-8 rounded-xl bg-gradient-to-br flex items-center justify-center',
              'from-primary via-primary to-emerald-400'
            )}>
            <span className='text-white font-black text-sm tracking-tight'>IN</span>
          </div>
        ) : (
          <img
            src='/logo.png'
            alt='Inovisum Logo'
            className='h-8 w-auto object-contain'
            style={{ maxWidth: 'none' }}
          />
        )
      ) : (
        <>
          <div
            className={cn(
              'w-8 h-8 rounded-xl bg-gradient-to-br flex items-center justify-center',
              'from-primary via-primary to-emerald-400'
            )}>
            {currentOrganization?.logoUrl ? (
              <img
                src={currentOrganization.logoUrl}
                alt={currentOrganization.name}
                className='w-full h-full rounded-xl object-cover'
              />
            ) : (
              <span className='text-white font-black text-sm tracking-tight'>
                {currentOrganization ? getInitials(currentOrganization.name) : 'IN'}
              </span>
            )}
          </div>
          {/* Name - animates out immediately when breadcrumb shows, delays in when hiding */}
          <div
            className={cn(
              'overflow-hidden ease-out',
              // Animate out immediately (duration-300), animate in with delay (after breadcrumb hides)
              showOrgName
                ? 'max-w-[200px] opacity-100 transition-all duration-300 delay-300'
                : 'max-w-0 opacity-0 transition-all duration-300 delay-0'
            )}>
            <span className='font-semibold text-lg tracking-tight whitespace-nowrap'>
              {currentOrganization?.name ?? ''}
            </span>
          </div>
        </>
      )}
    </>
  );

  // Render static button during SSR to prevent Radix hydration mismatch
  if (!mounted) {
    return (
      <button
        className={cn(
          'org-selector-trigger flex items-center gap-2 group rounded-xl transition-colors focus-visible:outline-none',
          'px-0 py-0',
          'cursor-default'
        )}>
        {triggerContent}
      </button>
    );
  }

  return (
    <>
      <style jsx global>{`
        [data-radix-popper-content-wrapper]:has(.org-selector-dropdown) {
          top: -29px !important;
          left: -13px !important;
        }
      `}</style>
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'org-selector-trigger flex items-center gap-2 group rounded-xl transition-colors focus-visible:outline-none',
              'px-0 py-0',
              !isInteractive && 'cursor-default'
            )}>
            {triggerContent}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' sideOffset={-20} className='org-selector-dropdown w-64 rounded-t-none'>
          {/* Header section - shows current org (same as trigger) */}
          <div className='org-selector-header px-2 py-2.5 flex items-center gap-2 h-14'>
            <LuChevronsUpDown className='h-4 w-4 text-foreground-subtle flex-shrink-0' />
            {isPlatformOrg ? (
              <img
                src='/logo.png'
                alt='Inovisum Logo'
                className='h-8 w-auto object-contain'
                style={{ maxWidth: 'none' }}
              />
            ) : currentOrganization ? (
              <>
                <div
                  className={cn(
                    'w-8 h-8 rounded-xl bg-gradient-to-br flex items-center justify-center',
                    'from-primary via-primary to-emerald-400'
                  )}>
                  {currentOrganization.logoUrl ? (
                    <img
                      src={currentOrganization.logoUrl}
                      alt={currentOrganization.name}
                      className='w-full h-full rounded-xl object-cover'
                    />
                  ) : (
                    <span className='text-white font-black text-sm tracking-tight'>
                      {getInitials(currentOrganization.name)}
                    </span>
                  )}
                </div>
                <span className='font-semibold text-lg tracking-tight'>{currentOrganization.name}</span>
              </>
            ) : null}
          </div>

          {/* Organization list - exclude current org (shown in header) */}
          {currentOrganization && organizations.filter((org) => org.id !== currentOrganization.id).length > 0 && (
            <>
              <DropdownMenuSeparator />
              {organizations
                .filter((org) => org.id !== currentOrganization.id)
                .map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => selectOrganization(org.id)}
                    className='flex items-center gap-3 px-3 py-2.5 cursor-pointer'>
                    {/* Avatar */}
                    <div
                      className={cn(
                        'w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center text-xs font-bold',
                        'from-primary via-primary to-emerald-400 text-primary-foreground'
                      )}>
                      {org.logoUrl ? (
                        <img src={org.logoUrl} alt={org.name} className='w-full h-full rounded-lg object-cover' />
                      ) : (
                        getInitials(org.name)
                      )}
                    </div>
                    <span className='flex-1 text-sm'>{org.name}</span>
                  </DropdownMenuItem>
                ))}
            </>
          )}

          <DropdownMenuSeparator />

          {/* Create organization option */}
          <DropdownMenuItem
            onClick={openCreateOrganization}
            className='flex items-center gap-2 px-3 py-2 cursor-pointer text-muted-foreground'>
            <span className='text-base'>+</span>
            Create organization
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export default OrganizationSelector;
